// Energy ROI Tracker - Portfolio-style approach to daily glycemic management
// Uses Glycemic Load (GL) for more accurate real-world impact tracking

export interface MealEntry {
  id: string;
  timestamp: Date;
  description: string;
  estimatedGI: number;
  estimatedGL: number; // Glycemic Load - the real impact based on serving size
  giLevel: "low" | "medium" | "high";
  glLevel: "low" | "medium" | "high"; // GL categories: low (0-10), medium (11-19), high (20+)
  context: "breakfast" | "lunch" | "dinner" | "snack" | "pre-workout" | "post-workout";
}

export interface DailyEnergy {
  date: string; // YYYY-MM-DD
  meals: MealEntry[];
  totalGIPoints: number; // Legacy - kept for backwards compatibility
  totalGLPoints: number; // New - actual glycemic load sum
  energyDebt: number; // Accumulated from high-GL choices
  balance: "surplus" | "neutral" | "deficit";
}

export interface EnergyAdvice {
  status: "green" | "yellow" | "red";
  headline: string;
  detail: string;
  recommendation: string;
}

// GL-based "cost" scoring system (more accurate than GI alone)
// Low GL (0-10): 1 point (minimal impact)
// Medium GL (11-19): 2 points (moderate impact)
// High GL (20+): 3 points (significant impact)
const GL_COSTS = {
  low: 1,
  medium: 2,
  high: 3,
};

// Daily GL budget: Target ~80 GL total (low-moderate GL diet)
// Converted to points: 8 points ideal (equivalent to 4 low-GL meals + snacks)
const DAILY_BUDGET = 8;
const WARNING_THRESHOLD = 10;
const DEFAULT_GL_TARGET = 80; // Total GL recommended for the day

// LocalStorage keys
const STORAGE_KEY = "energy_tracker_data";
const GL_TARGET_KEY = "gl_daily_target";

export function getGLTarget(): number {
  try {
    const stored = localStorage.getItem(GL_TARGET_KEY);
    if (stored) {
      const val = Number(stored);
      if (val >= 40 && val <= 200) return val;
    }
  } catch { /* ignore */ }
  return DEFAULT_GL_TARGET;
}

export function setGLTarget(target: number): void {
  localStorage.setItem(GL_TARGET_KEY, String(Math.round(Math.max(40, Math.min(200, target)))));
}

export function getTodayKey(): string {
  return new Date().toISOString().split("T")[0];
}

export function loadDailyEnergy(): DailyEnergy {
  const today = getTodayKey();
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      if (data.date === today) {
        // Ensure backwards compatibility - add totalGLPoints if missing
        if (data.totalGLPoints === undefined) {
          data.totalGLPoints = data.meals.reduce(
            (sum: number, m: MealEntry) => sum + (m.estimatedGL || 0),
            0
          );
        }
        return data;
      }
    }
  } catch {
    // Ignore errors
  }
  // Return fresh day
  return {
    date: today,
    meals: [],
    totalGIPoints: 0,
    totalGLPoints: 0,
    energyDebt: 0,
    balance: "surplus",
  };
}

export function saveDailyEnergy(energy: DailyEnergy): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(energy));
}

export function recordMealGI(
  description: string,
  estimatedGI: number,
  context: MealEntry["context"],
  estimatedGL?: number
): DailyEnergy {
  const energy = loadDailyEnergy();

  // GI level (for display/reference)
  const giLevel: MealEntry["giLevel"] =
    estimatedGI <= 55 ? "low" : estimatedGI <= 69 ? "medium" : "high";

  // If GL not provided, estimate it based on GI (assume typical serving)
  // This is a rough estimate: low GI foods tend to have lower GL
  const gl = estimatedGL ?? Math.round(estimatedGI * 0.15); // Rough heuristic

  // GL level (for actual impact scoring)
  const glLevel: MealEntry["glLevel"] =
    gl <= 10 ? "low" : gl <= 19 ? "medium" : "high";

  const meal: MealEntry = {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date(),
    description,
    estimatedGI,
    estimatedGL: gl,
    giLevel,
    glLevel,
    context,
  };

  energy.meals.push(meal);

  // Recalculate totals using GL-based scoring (more accurate)
  energy.totalGIPoints = energy.meals.reduce(
    (sum, m) => sum + GL_COSTS[m.glLevel || m.giLevel], // Fallback to giLevel for old entries
    0
  );

  // Calculate total GL for display
  energy.totalGLPoints = energy.meals.reduce(
    (sum, m) => sum + (m.estimatedGL || 0),
    0
  );

  // Calculate energy debt (points above budget)
  energy.energyDebt = Math.max(0, energy.totalGIPoints - DAILY_BUDGET);

  // Determine balance based on GL scoring
  if (energy.totalGIPoints <= DAILY_BUDGET) {
    energy.balance = "surplus";
  } else if (energy.totalGIPoints <= WARNING_THRESHOLD) {
    energy.balance = "neutral";
  } else {
    energy.balance = "deficit";
  }

  saveDailyEnergy(energy);
  return energy;
}

export function removeMeal(mealId: string): DailyEnergy {
  const energy = loadDailyEnergy();
  energy.meals = energy.meals.filter((m) => m.id !== mealId);

  // Recalculate totals
  energy.totalGIPoints = energy.meals.reduce(
    (sum, m) => sum + GL_COSTS[m.glLevel || m.giLevel],
    0
  );
  energy.totalGLPoints = energy.meals.reduce(
    (sum, m) => sum + (m.estimatedGL || 0),
    0
  );
  energy.energyDebt = Math.max(0, energy.totalGIPoints - DAILY_BUDGET);

  if (energy.totalGIPoints <= DAILY_BUDGET) {
    energy.balance = "surplus";
  } else if (energy.totalGIPoints <= WARNING_THRESHOLD) {
    energy.balance = "neutral";
  } else {
    energy.balance = "deficit";
  }

  saveDailyEnergy(energy);
  return energy;
}

export function getEnergyAdvice(energy: DailyEnergy): EnergyAdvice {
  const hour = new Date().getHours();
  const isPostWorkoutWindow = hour >= 6 && hour <= 9 || hour >= 16 && hour <= 19;
  const remaining = Math.max(0, DAILY_BUDGET - energy.totalGIPoints);
  const totalGL = energy.totalGLPoints || 0;
  const glRemaining = Math.max(0, getGLTarget() - totalGL);

  // Morning - fresh start
  if (hour < 10 && energy.meals.length === 0) {
    return {
      status: "green",
      headline: "Fresh energy budget!",
      detail: `${DAILY_BUDGET} points available (GL target: ${getGLTarget()})`,
      recommendation: "Start with a protein-rich, low-GL breakfast to set the tone.",
    };
  }

  // Strong position - under budget
  if (energy.totalGIPoints < DAILY_BUDGET - 2) {
    if (isPostWorkoutWindow) {
      return {
        status: "green",
        headline: "Great position to spend!",
        detail: `${remaining} points left • ~${glRemaining} GL remaining`,
        recommendation: "Post-workout is the perfect time for higher-GL foods. Your muscles are primed to absorb glucose.",
      };
    }
    return {
      status: "green",
      headline: "Energy portfolio healthy",
      detail: `${remaining} points left • Total GL: ${totalGL}`,
      recommendation: "Keep investing in low-GL options to maintain stable energy throughout the day.",
    };
  }

  // Neutral - at or slightly over budget
  if (energy.totalGIPoints <= WARNING_THRESHOLD) {
    return {
      status: "yellow",
      headline: "Budget nearly spent",
      detail: energy.energyDebt > 0 ? `${energy.energyDebt} points over • GL: ${totalGL}` : `At daily limit • GL: ${totalGL}`,
      recommendation: "Hedge with protein and fiber for remaining meals. Consider a walk to help process recent carbs.",
    };
  }

  // Over budget - energy debt
  return {
    status: "red",
    headline: "Energy debt accumulating",
    detail: `${energy.energyDebt} points over • Total GL: ${totalGL}`,
    recommendation: "Focus on protein-only snacks and a walk. Tomorrow is a fresh start - no guilt, just awareness.",
  };
}

export function getTimeContextRecommendation(): string {
  const hour = new Date().getHours();

  if (hour >= 6 && hour < 10) {
    return "Morning: Great time for complex carbs - your body processes glucose best now.";
  }
  if (hour >= 10 && hour < 12) {
    return "Late morning: Energy from breakfast fading? A small protein snack maintains focus.";
  }
  if (hour >= 12 && hour < 14) {
    return "Lunch: Balance protein with carbs. Avoid heavy starches to prevent afternoon slump.";
  }
  if (hour >= 14 && hour < 17) {
    return "Afternoon: The danger zone! Low-GI snacks prevent the 3pm crash.";
  }
  if (hour >= 17 && hour < 19) {
    return "Pre-dinner: If exercising, this is a good time for strategic higher-GI fuel.";
  }
  if (hour >= 19 && hour < 21) {
    return "Dinner: Last major meal. Protein and veggies help overnight recovery.";
  }
  return "Evening: Body is winding down. Light, low-GI snacks if needed.";
}

export function calculateROI(energy: DailyEnergy): { percentage: number; label: string } {
  // Simple: what percentage of the daily GL budget has been used?
  const totalGL = energy.totalGLPoints || 0;

  if (energy.meals.length === 0) {
    return { percentage: 0, label: "Nothing logged yet" };
  }

  const pct = Math.round((totalGL / getGLTarget()) * 100);

  let label = "On track";
  if (pct >= 100) label = "Over budget";
  else if (pct >= 80) label = "Nearly full";
  else if (pct >= 50) label = "Halfway there";
  else label = "Plenty of room";

  return { percentage: Math.min(pct, 150), label };
}
